import { Minus, Plus } from "@phosphor-icons/react";
import { forwardRef, useState, type InputHTMLAttributes } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { twRing } from "@/lib/emotionTw";

export type NumberStepperProps = Omit<InputHTMLAttributes<HTMLInputElement>, "defaultValue" | "onChange" | "size" | "value"> & {
  value?: number;
  defaultValue?: number;
  onValueChange?: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  size?: "sm" | "md";
  error?: boolean;
};

type NumberStepperSize = "sm" | "md";

interface NumberStepperOwnerState {
  size: NumberStepperSize;
  error: boolean;
  disabled: boolean;
}

/* Exact compiled output of the pre-migration Tailwind utilities, in
 * cva/tailwind-merge emission order. Verified against the frozen baseline. */

const stepperSizeStyles: Record<NumberStepperSize, CSSObject> = {
  sm: { height: "2rem", fontSize: ".75rem", lineHeight: "1rem" },
  md: { height: "2.25rem", fontSize: ".875rem", lineHeight: "1.25rem" }
};

const stepperRootBase: CSSObject = {
  display: "inline-flex",
  alignItems: "center",
  overflow: "hidden",
  borderRadius: "var(--bfrs-input-radius)",
  borderWidth: "1px",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
  transitionProperty:
    "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  transitionDuration: ".15s",
  "&:focus-within": twRing("2px", "hsl(var(--bfrs-color-border-focus) / .2)")
};

const stepperRootError: CSSObject = {
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))",
  "&:focus-within": {
    "--tw-ring-color": "hsl(var(--bfrs-color-danger) / .15)"
  }
};

const stepperRootDefault: CSSObject = {
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-form-control-border) / var(--tw-border-opacity, 1))",
  "&:focus-within": {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))"
  }
};

const stepperRootDisabled: CSSObject = {
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
};

const NumberStepperRoot = styled("div", {
  label: "AgenticNumberStepper-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: NumberStepperOwnerState }>(({ ownerState }) => [
  stepperRootBase,
  ownerState.error ? stepperRootError : stepperRootDefault,
  ownerState.disabled && stepperRootDisabled
]);

const NumberStepperButton = styled("button", {
  label: "AgenticNumberStepper-stepButton",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: NumberStepperOwnerState }>(({ ownerState }) => [
  {
    display: "flex",
    aspectRatio: "1 / 1",
    alignItems: "center",
    justifyContent: "center",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))",
    "&:hover": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))"
    },
    "&:disabled": {
      cursor: "not-allowed",
      opacity: ".5"
    }
  },
  stepperSizeStyles[ownerState.size]
]);

const NumberStepperInput = styled("input", {
  label: "AgenticNumberStepper-input",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: NumberStepperOwnerState }>(({ ownerState }) => [
  {
    width: "3.5rem",
    borderLeftWidth: "1px",
    borderRightWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-form-control-border) / var(--tw-border-opacity, 1))",
    backgroundColor: "transparent",
    textAlign: "center",
    fontWeight: 600,
    outline: "2px solid transparent",
    outlineOffset: "2px",
    "&:disabled": {
      cursor: "not-allowed"
    }
  },
  stepperSizeStyles[ownerState.size]
]);

const clamp = (value: number, min?: number, max?: number) => {
  if (typeof min === "number" && value < min) return min;
  if (typeof max === "number" && value > max) return max;
  return value;
};

export const NumberStepper = forwardRef<HTMLInputElement, NumberStepperProps>(
  ({ className, value, defaultValue = 0, onValueChange, min, max, step = 1, size = "md", error, disabled, ...props }, ref) => {
    const [internalValue, setInternalValue] = useState(defaultValue);
    const currentValue = value ?? internalValue;
    const commitValue = (nextValue: number) => {
      const normalized = clamp(Number.isFinite(nextValue) ? nextValue : min ?? 0, min, max);
      if (value === undefined) setInternalValue(normalized);
      onValueChange?.(normalized);
    };
    const decrementDisabled = disabled || (typeof min === "number" && currentValue <= min);
    const incrementDisabled = disabled || (typeof max === "number" && currentValue >= max);
    const ownerState: NumberStepperOwnerState = {
      size,
      error: Boolean(error),
      disabled: Boolean(disabled)
    };

    return (
      <NumberStepperRoot ownerState={ownerState} className={className}>
        <NumberStepperButton
          type="button"
          disabled={decrementDisabled}
          ownerState={ownerState}
          onClick={() => commitValue(currentValue - step)}
          aria-label="Decrease value"
        >
          <Minus size={14} weight="bold" />
        </NumberStepperButton>
        <NumberStepperInput
          ref={ref}
          type="number"
          value={currentValue}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          aria-invalid={error || undefined}
          ownerState={ownerState}
          onChange={(event) => commitValue(Number(event.currentTarget.value))}
          {...props}
        />
        <NumberStepperButton
          type="button"
          disabled={incrementDisabled}
          ownerState={ownerState}
          onClick={() => commitValue(currentValue + step)}
          aria-label="Increase value"
        >
          <Plus size={14} weight="bold" />
        </NumberStepperButton>
      </NumberStepperRoot>
    );
  }
);

NumberStepper.displayName = "NumberStepper";
